// src/pages/Salary.jsx
import React, { useEffect, useState, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import {
  FaMoneyBillAlt, FaPlus, FaEye, FaMagic, FaEdit,
  FaChartLine, FaHistory, FaInfoCircle, FaCheckCircle, FaTimes,
  FaSave, FaUndo,
} from "react-icons/fa";
import SalarySlip from "../components/SalarySlip";
import { sumGrossBreakdown, computeTotalCTC, fmtINR, parseCTCNumber } from "../utils/salaryUtils";
import { notifyUser } from "../utils/notifications";
import styles from "./Salary.module.css";

const Salary = ({ selectedEmployee }) => {
  const { userData } = useAuth();
  const isManagementView = !!selectedEmployee;
  const userInFocus = isManagementView ? selectedEmployee : userData;

  const canManage =
    userData?.role === "superadmin" ||
    (userData?.role === "admin" && userInFocus?.role === "employee");

  // ─── view tabs: "slips" | "history" ───────────────────────────────────────
  const [activeTab, setActiveTab] = useState("slips");

  // ─── payroll list ──────────────────────────────────────────────────────────
  const [payroll, setPayroll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [showAdditional, setShowAdditional] = useState(false);

  // ─── compensation structure stored in employee_records_complete ───────────
  const [compStructure, setCompStructure] = useState(null);   // { annualCTC, breakdown, history[] }
  const [structureLoading, setStructureLoading] = useState(false);

  // ─── auto-fill state ───────────────────────────────────────────────────────
  const [autoFilled, setAutoFilled] = useState(false);   // was current form auto-filled?
  const [leaveBalance, setLeaveBalance] = useState(null);
  const currentYear = new Date().getFullYear();

  const initialFormState = {
    month: "",
    payDate: "",
    basic: "",
    hra: "",
    lta: "",
    medicalReimbursement: "",
    telephoneAllowance: "",
    specialAllowance: "",
    professionalTax: "",
    providentFund: "",
    incomeTax: "",
    variablePay: "",
    retentionTranche: "",
    retentionTrancheYear: "1",
    joiningBonus: "",
    otherLabel: "",
    otherAmount: "",
    deductionOtherLabel: "",
    deductionOtherAmount: "",
  };

  const [newPayroll, setNewPayroll] = useState(initialFormState);

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  // ─── Fetch fixed salary breakdown + variable components from employee record ─────
  const fetchCompStructure = useCallback(async () => {
    if (!userInFocus?.employeeId) return;
    setStructureLoading(true);
    try {
      const ref = doc(db, "employee_records_complete", userInFocus.employeeId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data();
        // Fixed gross breakdown — no fallback formula; must be explicitly saved by admin
        const breakdown = d.salaryBreakdown && Number(d.salaryBreakdown.basic) > 0 ? d.salaryBreakdown : null;
        const variableComponents = d.variableComponents || {};
        const { monthlyGross, annualGross } = sumGrossBreakdown(breakdown);
        setCompStructure({
          annualCTC: breakdown ? computeTotalCTC(breakdown, variableComponents) : 0,
          grossMonthlySalary: d.grossMonthlySalary || "",
          breakdown,
          monthlyGross,
          annualGross,
          variableComponents,
          // salary event log: joining bonus paid, esop tranches paid, appraisals
          eventLog: Array.isArray(d.salaryEventLog) ? d.salaryEventLog : [],
          // appraisal history: [{ date, oldCTC, newCTC, note }]
          appraisalLog: Array.isArray(d.appraisalLog) ? d.appraisalLog : [],
        });
      }
    } catch (e) {
      console.warn("fetchCompStructure:", e);
    }
    setStructureLoading(false);
  }, [userInFocus?.employeeId]);

  useEffect(() => {
    if (!userInFocus?.uid) return;
    setLoading(true);
    const q = query(collection(db, "salary"), where("uid", "==", userInFocus.uid));
    const unsub = onSnapshot(q, (snap) => {
      setPayroll(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.payDate) - new Date(a.payDate)));
      setLoading(false);
    }, (err) => {
      console.error("Error fetching payroll:", err);
      showNotification("Could not fetch payroll data.", "error");
      setLoading(false);
    });
    return () => unsub();
  }, [userInFocus?.uid]);

  const addPayroll = async () => {
    const {
      month,
      payDate,
      basic,
      hra,
      lta,
      medicalReimbursement,
      telephoneAllowance,
      specialAllowance,
      professionalTax,
      providentFund,
      incomeTax,
    } = newPayroll;

    if (!month || !payDate || !basic) {
      return showNotification("Month, Pay Date, and Basic Salary are required.", "error");
    }

    setIsSubmitting(true);

    const earnings = [
      { name: "Basic", amount: parseFloat(basic) || 0 },
      { name: "HRA", amount: parseFloat(hra) || 0 },
      { name: "LTA", amount: parseFloat(lta) || 0 },
      { name: "Medical Allowance", amount: parseFloat(medicalReimbursement) || 0 },
      { name: "Telephone & Internet Allowance", amount: parseFloat(telephoneAllowance) || 0 },
      { name: "Special Allowance", amount: parseFloat(specialAllowance) || 0 },
    ];

    // Optional one-time / additional payments — appended only when filled
    if (parseFloat(newPayroll.variablePay) > 0)
      earnings.push({ name: "Variable Pay (Annual KPI Bonus)", amount: parseFloat(newPayroll.variablePay) });
    if (parseFloat(newPayroll.retentionTranche) > 0)
      earnings.push({ name: `Retention Bonus Tranche – Year ${newPayroll.retentionTrancheYear}`, amount: parseFloat(newPayroll.retentionTranche) });
    if (parseFloat(newPayroll.joiningBonus) > 0)
      earnings.push({ name: "Joining / Relocation Bonus", amount: parseFloat(newPayroll.joiningBonus) });
    if (newPayroll.otherLabel.trim() && parseFloat(newPayroll.otherAmount) > 0)
      earnings.push({ name: newPayroll.otherLabel.trim(), amount: parseFloat(newPayroll.otherAmount) });

    const deductions = [
      { name: "Professional Tax", amount: parseFloat(professionalTax) || 0 },
      { name: "Provident Fund (EPF)", amount: parseFloat(providentFund) || 0 },
      { name: "Income Tax (TDS)", amount: parseFloat(incomeTax) || 0 },
    ];
    if (newPayroll.deductionOtherLabel.trim() && parseFloat(newPayroll.deductionOtherAmount) > 0)
      deductions.push({ name: newPayroll.deductionOtherLabel.trim(), amount: parseFloat(newPayroll.deductionOtherAmount) });

    const grossSalary = earnings.reduce((sum, item) => sum + item.amount, 0);
    const totalDeductions = deductions.reduce((sum, item) => sum + item.amount, 0);
    const netSalary = grossSalary - totalDeductions;
    // Fixed gross (excl. one-time additions) for syncing grossMonthlySalary
    const monthlyGross = parseFloat(basic) + parseFloat(hra || 0) + parseFloat(lta || 0)
      + parseFloat(medicalReimbursement || 0) + parseFloat(telephoneAllowance || 0) + parseFloat(specialAllowance || 0);

    try {
      // 1. Add salary slip to salary collection
      await addDoc(collection(db, "salary"), {
        uid: userInFocus.uid,
        createdAt: serverTimestamp(),
        month,
        payDate,
        earnings,
        deductions,
        grossSalary,
        totalDeductions,
        netSalary,
      });

      // 2. ✨ Sync salary info + event log to employee_records_complete
      try {
        const employeeRecordRef = doc(db, "employee_records_complete", userInFocus.employeeId);
        const salaryHistoryEntry = { month, payDate, grossSalary, netSalary, totalDeductions, addedAt: new Date().toISOString() };

        const updatePayload = {
          grossMonthlySalary: String(Math.round(monthlyGross || netSalary)),
          lastSalaryUpdateMonth: month,
          lastSalaryUpdateDate: payDate,
          [`salaryHistory.${month.replace(/[\s/]/g, '_')}`]: salaryHistoryEntry,
          updatedAt: serverTimestamp(),
        };

        // Append one-time events to the event log
        const newEvents = [];
        const addEvt = (type, label, amount) => newEvents.push({
          type, label, amount, month, payDate,
          recordedAt: new Date().toISOString(),
          recordedBy: userData?.name || userData?.email || "admin",
        });
        if (parseFloat(newPayroll.joiningBonus) > 0)
          addEvt("joining_bonus",  "Joining / Relocation Bonus", parseFloat(newPayroll.joiningBonus));
        if (parseFloat(newPayroll.retentionTranche) > 0)
          addEvt("retention_tranche", `Retention Bonus Tranche – Year ${newPayroll.retentionTrancheYear}`, parseFloat(newPayroll.retentionTranche));
        if (parseFloat(newPayroll.variablePay) > 0)
          addEvt("variable_pay",   "Variable Pay (Annual KPI Bonus)", parseFloat(newPayroll.variablePay));
        if (newPayroll.otherLabel.trim() && parseFloat(newPayroll.otherAmount) > 0)
          addEvt("other",          newPayroll.otherLabel.trim(), parseFloat(newPayroll.otherAmount));

        if (newEvents.length > 0)
          updatePayload.salaryEventLog = arrayUnion(...newEvents);

        await updateDoc(employeeRecordRef, updatePayload);
        console.log("✅ Salary info synced to Employee List");
      } catch (syncErr) {
        console.warn("Could not sync salary to employee_records_complete:", syncErr);
      }

      setNewPayroll(initialFormState);
      setAutoFilled(false);
      setShowAdditional(false);
      // Notify the employee that their salary slip is ready
      if (userInFocus?.uid && userInFocus.uid !== userData?.uid) {
        notifyUser({
          recipientUid: userInFocus.uid,
          type: 'salary_slip',
          title: 'Salary Slip Generated',
          body: `Your salary slip for ${month} is ready. Net pay: ₹${netSalary?.toLocaleString('en-IN') || '—'}.`,
          link: '/dashboard',
          senderUid: userData?.uid,
          senderName: userData?.name || '',
          metadata: { month, netSalary },
        });
      }
      showNotification("Payroll added successfully and synced to Employee List!");
      fetchCompStructure();
    } catch (error) {
      console.error("Error adding payroll:", error);
      showNotification("Failed to add payroll.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (slipId) => {
    if (!window.confirm("Are you sure you want to delete this salary slip permanently?"))
      return;
    setIsSubmitting(true);
    try {
      await deleteDoc(doc(db, "salary", slipId));
      showNotification("Salary slip deleted successfully.");
      setSelectedSlip(null);
    } catch (error) {
      console.error("Error deleting slip:", error);
      showNotification("Failed to delete slip.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Auto-fill form from the employee's FIXED salary breakdown (no formula) ───
  const handleAutoFill = useCallback(() => {
    if (!compStructure?.breakdown) {
      showNotification("No fixed salary breakdown saved for this employee. Set it via 'Edit Salary Breakdown' first.", "error");
      return;
    }
    const b = compStructure.breakdown;
    const today = new Date();
    const monthStr = today.toLocaleString("en-IN", { timeZone: 'Asia/Kolkata', month: "long", year: "numeric" });
    const payDateStr = today.toISOString().split("T")[0];
    setNewPayroll(prev => ({
      ...prev,
      month:               prev.month    || monthStr,
      payDate:             prev.payDate  || payDateStr,
      basic:               String(b.basic),
      hra:                 String(b.hra),
      lta:                 String(b.lta),
      medicalReimbursement: String(b.medical),
      telephoneAllowance:  String(b.telephone),
      specialAllowance:    String(b.special),
      professionalTax:     "",
      providentFund:       "",
      incomeTax:           "",
    }));
    setAutoFilled(true);
    setShowAdditional(false);
    showNotification(`✅ Auto-filled from saved breakdown (Monthly Gross ₹${fmtINR(compStructure.monthlyGross)}). Review & adjust deductions, then save.`, "success");
  }, [compStructure]);

  useEffect(() => {
    if (userInFocus?.uid) {
      fetchCompStructure();
    }
  }, [userInFocus, fetchCompStructure]);

  // ─── Real-time leave balance for the slip ──────────────────────────────────
  useEffect(() => {
    if (!userInFocus?.uid) return;
    const balRef = doc(db, "leave_balances", `${userInFocus.uid}_${currentYear}`);
    const unsub = onSnapshot(balRef, (snap) => {
      setLeaveBalance(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [userInFocus?.uid, currentYear]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount || 0);

  // ─── Salary event log helpers (joining bonus / ESOP / appraisal) ───────────
  const appendSalaryEvent = async (eventObj) => {
    if (!userInFocus?.employeeId) return;
    try {
      await updateDoc(doc(db, "employee_records_complete", userInFocus.employeeId), {
        salaryEventLog: arrayUnion({ ...eventObj, recordedAt: new Date().toISOString(), recordedBy: userData?.name || userData?.email || "admin" }),
        updatedAt: serverTimestamp(),
      });
      await fetchCompStructure();
    } catch (e) { console.warn("appendSalaryEvent:", e); }
  };

  const renderSalaryList = () => (
    <>
      <h3 className={styles.title}>
        <FaMoneyBillAlt /> Salary & Payroll {isManagementView && `for ${userInFocus.name}`}
      </h3>

      {isManagementView && canManage && (
        <div className={`${styles.controlForm} ${styles.salaryGrid}`}>
          {/* ── Auto-fill banner ────────────────────────────────────── */}
          {isManagementView && canManage && (
            <div className={styles.autoFillBanner} style={{ gridColumn: "1 / -1" }}>
              {compStructure?.breakdown ? (
                <>
                  <div className={styles.autoFillInfo}>
                    <FaChartLine />
                    <span>
                      Fixed Monthly Gross on file: <strong>₹{fmtINR(compStructure.monthlyGross)}</strong>
                      {" "}→ Total CTC: <strong>₹{fmtINR(compStructure.annualCTC)}</strong>
                      {Number(compStructure.variableComponents?.variablePayPool) > 0 && (
                        <span style={{ marginLeft: 8, color: "#92400e", background: "#fef3c7", padding: "2px 8px", borderRadius: 4, fontSize: "0.85rem" }}>
                          + Max Variable Pay Pool: ₹{fmtINR(compStructure.variableComponents.variablePayPool)}/yr (KPI-based, paid at year-end)
                        </span>
                      )}
                      {Number(compStructure.variableComponents?.retentionPool) > 0 && (
                        <span style={{ marginLeft: 8, color: "#1d4ed8", background: "#eff6ff", padding: "2px 8px", borderRadius: 4, fontSize: "0.85rem" }}>
                          + Retention Pool: ₹{fmtINR(compStructure.variableComponents.retentionPool)} (5 chunks)
                        </span>
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.autoFillBtn}
                    onClick={handleAutoFill}
                    title="Pre-fill all monthly earnings from this employee's fixed salary breakdown (variable pay excluded — add manually at year-end only)"
                  >
                    <FaMagic /> Auto-Fill from Saved Breakdown
                  </button>
                </>
              ) : (
                <span className={styles.autoFillWarn}>
                  <FaInfoCircle /> No fixed salary breakdown saved for this employee — set it via "Edit Salary Breakdown" on the Employee Detail page first, or fill manually below.
                </span>
              )}
              {autoFilled && (
                <div className={styles.autoFillTag}>
                  <FaCheckCircle /> Auto-filled — all fields are editable
                  <button
                    type="button"
                    className={styles.clearAutoFill}
                    onClick={() => { setNewPayroll(initialFormState); setAutoFilled(false); }}
                    title="Clear and reset form"
                  >
                    <FaUndo /> Reset
                  </button>
                </div>
              )}
            </div>
          )}

          <h4 className={styles.formTitle}>Add New Payroll Record</h4>

          <input
            placeholder="Month (e.g., March 2026)"
            value={newPayroll.month}
            onChange={(e) => setNewPayroll({ ...newPayroll, month: e.target.value })}
          />
          <input
            type="date"
            value={newPayroll.payDate}
            onChange={(e) => setNewPayroll({ ...newPayroll, payDate: e.target.value })}
          />

          <h5 className={styles.formSubheading}>Earnings</h5>
          <input
            type="number"
            placeholder="Basic Salary"
            value={newPayroll.basic}
            onChange={(e) => setNewPayroll({ ...newPayroll, basic: e.target.value })}
          />
          <input
            type="number"
            placeholder="HRA (House Rent Allowance)"
            value={newPayroll.hra}
            onChange={(e) => setNewPayroll({ ...newPayroll, hra: e.target.value })}
          />
          <input
            type="number"
            placeholder="LTA (Leave Travel Allowance)"
            value={newPayroll.lta}
            onChange={(e) => setNewPayroll({ ...newPayroll, lta: e.target.value })}
          />
          <input
            type="number"
            placeholder="Medical Allowance"
            value={newPayroll.medicalReimbursement}
            onChange={(e) => setNewPayroll({ ...newPayroll, medicalReimbursement: e.target.value })}
          />
          <input
            type="number"
            placeholder="Telephone & Internet Allowance"
            value={newPayroll.telephoneAllowance}
            onChange={(e) => setNewPayroll({ ...newPayroll, telephoneAllowance: e.target.value })}
          />
          <input
            type="number"
            placeholder="Special Allowance"
            value={newPayroll.specialAllowance}
            onChange={(e) => setNewPayroll({ ...newPayroll, specialAllowance: e.target.value })}
          />

          {/* live preview of gross */}
          {newPayroll.basic && (
            <div className={styles.grossPreview} style={{ gridColumn: "1 / -1" }}>
              Monthly Gross (earnings):&nbsp;
              <strong>{formatCurrency(
                [newPayroll.basic, newPayroll.hra, newPayroll.lta,
                 newPayroll.medicalReimbursement, newPayroll.telephoneAllowance, newPayroll.specialAllowance]
                .reduce((s, v) => s + (parseFloat(v) || 0), 0)
              )}</strong>
            </div>
          )}

          <h5 className={styles.formSubheading}>Deductions</h5>
          <input
            type="number"
            placeholder="Professional Tax"
            value={newPayroll.professionalTax}
            onChange={(e) => setNewPayroll({ ...newPayroll, professionalTax: e.target.value })}
          />
          <input
            type="number"
            placeholder="Provident Fund (EPF)"
            value={newPayroll.providentFund}
            onChange={(e) => setNewPayroll({ ...newPayroll, providentFund: e.target.value })}
          />
          <input
            type="number"
            placeholder="Income Tax (TDS)"
            value={newPayroll.incomeTax}
            onChange={(e) => setNewPayroll({ ...newPayroll, incomeTax: e.target.value })}
          />
          <input
            type="text"
            placeholder="Other Deduction – Description (e.g. Salary Advance Recovery)"
            value={newPayroll.deductionOtherLabel}
            onChange={(e) => setNewPayroll({ ...newPayroll, deductionOtherLabel: e.target.value })}
          />
          <input
            type="number"
            placeholder="Other Deduction – Amount"
            value={newPayroll.deductionOtherAmount}
            onChange={(e) => setNewPayroll({ ...newPayroll, deductionOtherAmount: e.target.value })}
          />

          <div style={{ gridColumn: "1 / -1" }}>
            <button
              type="button"
              className={styles.toggleAdditionalBtn}
              onClick={() => setShowAdditional((s) => !s)}
            >
              {showAdditional ? "▲ Hide" : "▼ Add"} Additional / One-Time Payments
            </button>
          </div>

          {showAdditional && (
            <>
              <h5
                className={`${styles.formSubheading} ${styles.formSubheadingAccent}`}
                style={{ gridColumn: "1 / -1" }}
              >
                Additional / One-Time Payments
                <span style={{ fontWeight: 400, fontSize: "0.85rem", marginLeft: 8 }}>
                  (Variable Pay, Retention Bonus, Joining Bonus, etc.) — logged to compensation history
                </span>
              </h5>
              {Number(compStructure?.variableComponents?.variablePayPool) > 0 && (
                <div style={{ gridColumn: "1 / -1", padding: "8px 14px", background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 6, fontSize: "0.87rem", color: "#78350f" }}>
                  <strong>⚠️ Variable Pay Note:</strong> Max Variable Pay Pool for this employee = <strong>₹{fmtINR(compStructure.variableComponents.variablePayPool)}/yr</strong>.
                  Enter actual release amount below (= Pool × Performance KPI%). Only add at <strong>end of financial year (April)</strong> — do NOT include in regular monthly payslips.
                </div>
              )}
              <input
                type="number"
                placeholder="Variable Pay (Annual KPI Bonus — year-end only)"
                value={newPayroll.variablePay}
                onChange={(e) => setNewPayroll({ ...newPayroll, variablePay: e.target.value })}
                style={{ gridColumn: "1 / -1" }}
              />
              <input
                type="number"
                placeholder="Retention Bonus Tranche (₹)"
                value={newPayroll.retentionTranche}
                onChange={(e) => setNewPayroll({ ...newPayroll, retentionTranche: e.target.value })}
              />
              <select
                value={newPayroll.retentionTrancheYear}
                onChange={(e) => setNewPayroll({ ...newPayroll, retentionTrancheYear: e.target.value })}
              >
                <option value="1">Retention Bonus – Year 1</option>
                <option value="2">Retention Bonus – Year 2</option>
                <option value="3">Retention Bonus – Year 3</option>
                <option value="4">Retention Bonus – Year 4</option>
                <option value="5">Retention Bonus – Year 5</option>
              </select>
              <input
                type="number"
                placeholder="Joining / Relocation Bonus"
                value={newPayroll.joiningBonus}
                onChange={(e) => setNewPayroll({ ...newPayroll, joiningBonus: e.target.value })}
                style={{ gridColumn: "1 / -1" }}
              />
              <input
                type="text"
                placeholder="Other – Description (e.g. Project Incentive)"
                value={newPayroll.otherLabel}
                onChange={(e) => setNewPayroll({ ...newPayroll, otherLabel: e.target.value })}
              />
              <input
                type="number"
                placeholder="Other – Amount"
                value={newPayroll.otherAmount}
                onChange={(e) => setNewPayroll({ ...newPayroll, otherAmount: e.target.value })}
              />
            </>
          )}

          <button
            onClick={addPayroll}
            className={styles.actionBtnPrimary}
            disabled={isSubmitting}
            style={{ marginTop: "10px" }}
          >
            {isSubmitting ? "Saving..." : <><FaSave /> Save Payslip</>}
          </button>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Month</th>
                <th>Pay Date</th>
                <th>Gross Salary</th>
                <th>Net Salary</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payroll.length > 0 ? (
                payroll.map((p) => (
                  <tr
                    key={p.id}
                    className={styles.clickableRow}
                    onClick={() => setSelectedSlip(p)}
                  >
                    <td data-label="Month">{p.month}</td>
                    <td data-label="Pay Date">{p.payDate}</td>
                    <td data-label="Gross Salary">{formatCurrency(p.grossSalary)}</td>
                    <td data-label="Net Salary">{formatCurrency(p.netSalary)}</td>
                    <td data-label="Actions">
                      <button className={styles.actionBtnPrimary} data-tooltip="View Slip" onClick={(e) => { e.stopPropagation(); setSelectedSlip(p); }}>
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">No payroll records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  // ─── Compensation History tab ────────────────────────────────────────────────
  const EVENT_ICONS = {
    joining_bonus:     "🎁",
    retention_tranche: "📈",
    variable_pay:      "🎯",
    appraisal:         "⬆️",
    other:             "💸",
  };

  const renderHistory = () => (
    <>
      <h3 className={styles.title}><FaHistory /> Compensation History {isManagementView && `— ${userInFocus.name}`}</h3>

      {/* ── CTC / Appraisal timeline ─────────────────── */}
      {compStructure?.appraisalLog?.length > 0 && (
        <div className={styles.historySection}>
          <h4 className={styles.historyHeading}>Salary Revisions / Appraisals</h4>
          <div className={styles.timelineList}>
            {[...compStructure.appraisalLog].reverse().map((a, i) => (
              <div key={i} className={styles.timelineItem}>
                <div className={styles.timelineDot}>⬆️</div>
                <div className={styles.timelineBody}>
                  <div className={styles.timelineTitle}>
                    CTC revised to <strong>₹{fmtINR(parseCTCNumber(a.newCTC))}</strong>
                    {a.oldCTC && <span className={styles.timelineOld}> (from ₹{fmtINR(parseCTCNumber(a.oldCTC))})</span>}
                  </div>
                  <div className={styles.timelineMeta}>{a.date}{a.note ? ` · ${a.note}` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── One-time payment event log ───────────────── */}
      {compStructure?.eventLog?.length > 0 && (
        <div className={styles.historySection}>
          <h4 className={styles.historyHeading}>One-Time Payments Disbursed</h4>
          <div className={styles.tableWrapper}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Month</th>
                  <th>Pay Date</th>
                  <th>Recorded By</th>
                </tr>
              </thead>
              <tbody>
                {[...compStructure.eventLog].reverse().map((e, i) => (
                  <tr key={i}>
                    <td>{EVENT_ICONS[e.type] || "💸"} {e.type?.replace(/_/g, " ")}</td>
                    <td>{e.label}</td>
                    <td><strong>{formatCurrency(e.amount)}</strong></td>
                    <td>{e.month}</td>
                    <td>{e.payDate}</td>
                    <td>{e.recordedBy || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Monthly payslip summary ──────────────────── */}
      <div className={styles.historySection}>
        <h4 className={styles.historyHeading}>Monthly Payslip Summary</h4>
        {loading ? <p>Loading...</p> : payroll.length === 0 ? <p>No payslips yet.</p> : (
          <div className={styles.tableWrapper}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Pay Date</th>
                  <th>Gross</th>
                  <th>Deductions</th>
                  <th>Net (Take-Home)</th>
                </tr>
              </thead>
              <tbody>
                {payroll.map((p) => (
                  <tr key={p.id} className={styles.clickableRow} onClick={() => { setSelectedSlip(p); setActiveTab("slips"); }}>
                    <td>{p.month}</td>
                    <td>{p.payDate}</td>
                    <td>{formatCurrency(p.grossSalary)}</td>
                    <td>{formatCurrency(p.totalDeductions)}</td>
                    <td><strong>{formatCurrency(p.netSalary)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(!compStructure?.appraisalLog?.length && !compStructure?.eventLog?.length) && (
        <p style={{ color: "#9CA3AF", marginTop: 16 }}>
          No compensation events logged yet. One-time payments (Variable Pay, ESOP, Joining Bonus) will appear here when added to a payslip.
        </p>
      )}
    </>
  );

  return (
    <div className={styles.salaryComponentContainer}>
      {notification.show && (
        <div className={`${styles.notification} ${notification.type === "success" ? styles.notificationSuccess : styles.notificationError}`}>
          {notification.message}
        </div>
      )}

      {selectedSlip ? (
        <SalarySlip
          slip={selectedSlip}
          employee={userInFocus}
          onBack={() => setSelectedSlip(null)}
          onDelete={handleDelete}
          isDeletable={isManagementView && canManage}
          isDeleting={isSubmitting}
          breakdown={{
            ...compStructure?.breakdown,
            hasVariable:  Number(compStructure?.variableComponents?.variablePayPool) > 0,
            hasRetention: Number(compStructure?.variableComponents?.retentionPool) > 0,
            joiningBonus: Number(compStructure?.variableComponents?.joiningBonus) || 0,
          }}
          leaveBalance={leaveBalance}
          leaveYear={currentYear}
        />
      ) : (
        <>
          {/* ── Tab bar ──────────────────────────────────────────── */}
          <div className={styles.salaryTabs}>
            <button
              className={`${styles.salaryTab} ${activeTab === "slips" ? styles.salaryTabActive : ""}`}
              onClick={() => setActiveTab("slips")}
            >
              <FaMoneyBillAlt /> Payslips
            </button>
            <button
              className={`${styles.salaryTab} ${activeTab === "history" ? styles.salaryTabActive : ""}`}
              onClick={() => setActiveTab("history")}
            >
              <FaHistory /> Compensation History
            </button>
          </div>

          {activeTab === "slips"   && renderSalaryList()}
          {activeTab === "history" && renderHistory()}
        </>
      )}
    </div>
  );
};

export default Salary;
